// THREAD-WAKE: who a follow-up without an @mention goes to. The server decides (`threads.agents`
// `addressee_membership_id`, per conversation: a topic's top level, or a root and its replies);
// the composer shows it as a small "→ Riley" chip above the box. The chip's menu lists the agents
// taking part plus "Nobody (just a note)"; a pick rides on the next message only (`addressee`),
// and is sent only when it differs from the server's answer.
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  InfoTip,
} from "@companyos/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRightIcon, ChevronDownIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { threadAgentsQuery } from "./chat-queries";
import type { ChatContext } from "./use-chat";

/** "none" = a note (wakes no addressee); a membership id = that agent. */
export type AddresseePick = string;

const NOBODY = "none";

export type Addressee = {
  /** The server's addressee and the agents taking part; null: no chip. */
  shown: { current: string; name: string; options: { id: string; name: string }[] } | null;
  /** The person's pick (null: the server's). */
  pick: AddresseePick | null;
  setPick: (p: AddresseePick | null) => void;
  /** What to send as `addressee` with the next message (undefined: let the server pick). */
  outgoing: () => AddresseePick | undefined;
  /** After a send: forget the pick and ask the server again. */
  sent: () => void;
};

/**
 * A conversation's messages as one string that changes when a message is added, edited or
 * deleted (each may move the addressee). Optimistic local rows do not count.
 */
export function conversationVersion(
  rows: readonly { id: string; edited_at?: string | null; deleted_at?: string | null }[],
  extra: string | null = null,
): string | undefined {
  const parts = rows
    .filter((r) => !r.id.startsWith("local-"))
    .map((r) => `${r.id}:${r.edited_at ?? ""}:${r.deleted_at ?? ""}`);
  if (extra) parts.unshift(extra);
  return parts.length ? parts.join(",") : undefined;
}

/**
 * The conversation's addressee. `latestId`: the conversation's version (`conversationVersion`),
 * so an agent's reply, an edit or a deletion (which may move the addressee) refetches it.
 */
export function useAddressee(
  chat: ChatContext,
  threadId: string | null,
  root: string | null,
  latestId: string | undefined,
): Addressee {
  const { api, co } = chat;
  const queryClient = useQueryClient();
  const hasAgents = chat.mentionables.some((m) => m.kind === "agent");
  const options = threadAgentsQuery(api, co, threadId ?? "", root ?? undefined);
  const q = useQuery({
    ...options,
    enabled: hasAgents && threadId !== null && threadId !== "" && chat.meId !== null,
  });
  // The pick belongs to its conversation: another one starts on the server's answer.
  const conversation = `${threadId ?? ""}|${root ?? ""}`;
  const [picked, setPicked] = useState<{ at: string; pick: AddresseePick } | null>(null);
  const pick = picked && picked.at === conversation ? picked.pick : null;
  const setPick = useCallback(
    (p: AddresseePick | null) => setPicked(p === null ? null : { at: conversation, pick: p }),
    [conversation],
  );
  // A new message (an agent's reply, someone else's post) may move the addressee.
  const seen = useRef(latestId);
  const key = useMemo(
    () => threadAgentsQuery(api, co, threadId ?? "", root ?? undefined).queryKey,
    [api, co, threadId, root],
  );
  useEffect(() => {
    if (!latestId || seen.current === latestId) return;
    seen.current = latestId;
    void queryClient.invalidateQueries({ queryKey: key, exact: true });
  }, [latestId, queryClient, key]);
  const data = q.data;
  const server = data?.addressee_membership_id ?? null;
  const rows = data?.data ?? [];
  const nameOf = (id: string) =>
    rows.find((r) => r.membership_id === id)?.name ?? chat.members.get(id)?.name ?? "an agent";
  const opts = rows
    .filter((r) => r.participating || r.membership_id === server)
    .map((r) => ({ id: r.membership_id, name: r.name }));
  const current = pick ?? server;
  const shown =
    server !== null && current !== null
      ? { current, name: current === NOBODY ? "Nobody" : nameOf(current), options: opts }
      : null;
  const outgoing = () => (pick !== null && pick !== server ? pick : undefined);
  const sent = useCallback(() => {
    setPicked(null);
    void queryClient.invalidateQueries({ queryKey: key, exact: true });
  }, [queryClient, key]);
  return { shown, pick, setPick, outgoing, sent };
}

/** The "→ Riley" chip above a composer, its picker and its (i). Nothing when there is none. */
export function AddresseeChip({ addressee }: { addressee: Addressee }) {
  const { shown, setPick } = addressee;
  if (!shown) return null;
  const nobody = shown.current === NOBODY;
  return (
    <div data-slot="addressee" className="flex items-center gap-0.5 text-xs text-muted-foreground">
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="ghost"
              size="xs"
              aria-label={nobody ? "Goes to nobody: change" : `Goes to ${shown.name}: change`}
              className="h-6 gap-1 px-1.5 text-xs font-medium text-secondary hover:text-foreground"
            />
          }
        >
          <ArrowRightIcon aria-hidden="true" className="size-3" />
          <span className={nobody ? "italic" : undefined}>
            {nobody ? "Nobody (note)" : shown.name}
          </span>
          <ChevronDownIcon aria-hidden="true" className="size-3 opacity-60" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-48">
          <DropdownMenuRadioGroup value={shown.current} onValueChange={(v) => setPick(String(v))}>
            {shown.options.map((o) => (
              <DropdownMenuRadioItem key={o.id} value={o.id} closeOnClick>
                {o.name}
              </DropdownMenuRadioItem>
            ))}
            <DropdownMenuRadioItem value={NOBODY} closeOnClick>
              Nobody (just a note)
            </DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      <InfoTip label="About who this goes to">
        Messages here without an @mention go to this agent. Pick another, or Nobody to just leave a
        note.
      </InfoTip>
    </div>
  );
}
